import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { SMIIFY_API } from '../config/api';

const StoreContext = createContext();
const idOf = (s) => String(s?._id || s?.id || '');

export const StoreProvider = ({ children }) => {
  const { user } = useAuth();
  const [activeStore, setActiveStore] = useState(null);
  const [availableStores, setAvailableStores] = useState([]);

  useEffect(() => {
    if (!user) {
      setAvailableStores([]);
      setActiveStore(null);
      localStorage.removeItem('smiify_active_store_id');
      return;
    }

    let stores = (user.assignedStores || user.accessibleStores || []).map((s) => ({ ...s, id: idOf(s), _id: idOf(s) }));

    // Global HQ only for the brand owner
    if (user.role === 'SUPERADMIN') {
      stores = [{
        _id: 'global_hq',
        id: 'global_hq',
        storeName: user.partnerBusinessName || 'Global HQ',
        address: user.partnerAddress || 'Headquarters',
        isHQ: true,
      }, ...stores];
    }

    setAvailableStores(stores);
    if (!stores.length) { setActiveStore(null); return; }

    // keep the last branch if it is still allowed; otherwise the first one (HQ for owners)
    const savedId = localStorage.getItem('smiify_active_store_id');
    const saved = savedId ? stores.find((s) => idOf(s) === savedId) : null;
    const fallback = stores[0];
    const next = saved || fallback;
    setActiveStore(next);
    localStorage.setItem('smiify_active_store_id', idOf(next));
  }, [user]);

  const switchStore = useCallback((storeId) => {
    const store = availableStores.find((s) => idOf(s) === String(storeId));
    if (!store) return;
    setActiveStore(store);
    localStorage.setItem('smiify_active_store_id', idOf(store));
  }, [availableStores]);

  const activateStore = async (storeId) => {
    try {
      const token = localStorage.getItem('smiify_token');
      const response = await fetch(`${SMIIFY_API}/stores/${storeId}/activate`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) return false;
      setAvailableStores((prev) => prev.map((store) => {
        if (idOf(store) !== String(storeId)) return store;
        const updated = { ...store, smiifySubscription: { ...store.smiifySubscription, status: 'ACTIVE' } };
        if (activeStore && idOf(activeStore) === String(storeId)) setActiveStore(updated);
        return updated;
      }));
      return true;
    } catch (error) {
      console.error('Store activation failed', error);
      return false;
    }
  };

  return (
    <StoreContext.Provider value={{ activeStore, availableStores, switchStore, activateStore }}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => useContext(StoreContext);